import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useSnackbars } from '../../../app/error-handling/SnackBars';
import type { Comment, CommentAnchor, CommentTarget, CreateCommentAnchor } from '../../../data-contract/comment';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { allLanguageCommentsQueryKey, commentsQueryKey, commentsQueryKeyPrefix } from './use-comments-query';
import { useCurrentCommentAuthor } from './use-current-comment-author';
import { repliesQueryKey } from './use-replies-query';

type UseCreateCommentParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly language: string;
    readonly version: number;
};

export type CreateCommentVariables = {
    readonly id: string;
    readonly target: CommentTarget;
    readonly content: string;
    readonly anchor?: CommentAnchor | null;
};

export const toCreateCommentAnchor = (anchor: CommentAnchor): CreateCommentAnchor => ({
    positionSelector: anchor.positionSelector,
    quoteSelector: anchor.quoteSelector,
});

type CreateCommentContext = {
    readonly hadPreviousAllLanguageCommentsQuery: boolean;
    readonly previousComments: readonly Comment[] | undefined;
    readonly previousAllLanguageComments: readonly Comment[] | undefined;
    readonly previousReplies: readonly Comment[] | undefined;
    readonly hadPreviousRepliesQuery: boolean;
};

const upsertComment = (comments: readonly Comment[] | undefined, comment: Comment): readonly Comment[] => {
    if (is.undefined(comments)) return [comment];
    const index = comments.findIndex((cachedComment) => cachedComment.id === comment.id);
    if (index < 0) return [...comments, comment];
    const next = [...comments];
    next[index] = comment;
    return next;
};

export const useCreateComment = ({
    architectureId,
    itemId,
    language,
    version,
}: UseCreateCommentParams): UseMutationResult<Comment, Error, CreateCommentVariables, CreateCommentContext> => {
    const { createComment } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const author = useCurrentCommentAuthor();
    const { showErrorSnackbar } = useSnackbars();

    const commentsKey = commentsQueryKey({ architectureId, itemId, language, version });
    const allLanguageCommentsKey = allLanguageCommentsQueryKey({ architectureId, itemId, version });

    return useMutation<Comment, Error, CreateCommentVariables, CreateCommentContext>({
        mutationKey: ['create-comment', architectureId, itemId, version],
        mutationFn: async (variables) => {
            const result = await createComment({
                architectureId,
                body: {
                    content: variables.content,
                    id: variables.id,
                    ...(variables.target.kind === 'description-section' && !is.nullOrUndefined(variables.anchor)
                        ? { anchor: toCreateCommentAnchor(variables.anchor) }
                        : {}),
                    sourceLanguage: language,
                    target: variables.target,
                    version,
                },
                itemId,
            });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        onError: (error, variables, context) => {
            if (is.undefined(context)) return;
            queryClient.setQueryData<readonly Comment[]>(commentsKey, context.previousComments);
            if (context.hadPreviousAllLanguageCommentsQuery) {
                queryClient.setQueryData<readonly Comment[]>(
                    allLanguageCommentsKey,
                    context.previousAllLanguageComments,
                );
            } else {
                queryClient.removeQueries({ queryKey: allLanguageCommentsKey });
            }
            const repliesKey = repliesQueryKey({ architectureId, itemId, parentCommentId: variables.id, version });
            if (context.hadPreviousRepliesQuery) {
                queryClient.setQueryData<readonly Comment[]>(repliesKey, context.previousReplies);
            } else {
                queryClient.removeQueries({ queryKey: repliesKey });
            }
            showErrorSnackbar(error);
        },
        onMutate: async (variables) => {
            const repliesKey = repliesQueryKey({ architectureId, itemId, parentCommentId: variables.id, version });
            await Promise.all([
                queryClient.cancelQueries({ queryKey: commentsKey }),
                queryClient.cancelQueries({ queryKey: allLanguageCommentsKey }),
                queryClient.cancelQueries({ queryKey: repliesKey }),
            ]);

            const previousComments = queryClient.getQueryData<readonly Comment[]>(commentsKey);
            const previousAllLanguageComments = queryClient.getQueryData<readonly Comment[]>(allLanguageCommentsKey);
            const previousReplies = queryClient.getQueryData<readonly Comment[]>(repliesKey);
            const hadPreviousAllLanguageCommentsQuery = !is.undefined(
                queryClient.getQueryState(allLanguageCommentsKey),
            );
            const hadPreviousRepliesQuery = !is.undefined(queryClient.getQueryState(repliesKey));

            const optimisticComment: Comment = {
                author,
                content: variables.content,
                createdAt: new Date().toISOString(),
                id: variables.id,
                sourceLanguage: language,
                target: variables.target,
                version,
                anchor: variables.anchor ?? undefined,
            };

            queryClient.setQueryData<readonly Comment[]>(repliesKey, (prev) => prev ?? []);
            queryClient.setQueryData<readonly Comment[]>(commentsKey, (prev) => upsertComment(prev, optimisticComment));
            queryClient.setQueryData<readonly Comment[]>(allLanguageCommentsKey, (prev) =>
                upsertComment(prev, optimisticComment),
            );

            return {
                hadPreviousAllLanguageCommentsQuery,
                hadPreviousRepliesQuery,
                previousAllLanguageComments,
                previousComments,
                previousReplies,
            };
        },
        onSuccess: (data) => {
            queryClient.setQueryData<readonly Comment[]>(commentsKey, (prev) => upsertComment(prev, data));
            queryClient.setQueryData<readonly Comment[]>(allLanguageCommentsKey, (prev) => upsertComment(prev, data));
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: commentsQueryKeyPrefix(architectureId, itemId, version) });
        },
        throwOnError: false,
    });
};
